import React, { useEffect, useState, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTrainingLang } from '../i18n/TrainingLangContext';

const fetchSlideBlob = async (url) => {
  const token = localStorage.getItem('token');
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Slide load failed: ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
};

const COMMIT_MS = 260;

/**
 * @param variantKey Changing this throws away the cached slide images. The viewer
 *   caches blobs by slide number, and slide 1 of the Spanish deck is a different
 *   image from slide 1 of the English deck — without this, switching language would
 *   keep showing the previous language's slides.
 */
const SlideViewer = ({
  trainingId, slideNumbers, index, onIndexChange, slideUrl,
  extraFinalSlide = null, variantKey = '',
}) => {
  const { t } = useTrainingLang();
  const realCount = slideNumbers.length;
  const total = realCount + (extraFinalSlide ? 1 : 0);
  const isSynthetic = extraFinalSlide && index === realCount;

  const [urlsByNumber, setUrlsByNumber] = useState({});
  const [dragX, setDragX] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [error, setError] = useState('');

  const containerRef = useRef(null);
  const containerWidth = useRef(0);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const directionLock = useRef(null); // 'h' | 'v' | null
  // Mirrors the `animating` state for use inside touch handlers, which capture
  // stale `animating` if read directly from closure during a long-lived gesture.
  const animatingRef = useRef(false);
  // Mirrors urlsByNumber so the unmount cleanup can revoke the latest set of
  // blob URLs (an effect with [] deps closes over only the first render's value).
  const urlsRef = useRef({});
  urlsRef.current = urlsByNumber;

  // Measure container width — needed to convert drag-px into commit targets.
  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        containerWidth.current = containerRef.current.offsetWidth;
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Ensure prev / current / next slide blobs are loaded. Synthetic positions
  // are skipped (they render the `extraFinalSlide` ReactNode, not an image).
  useEffect(() => {
    let cancelled = false;
    setError('');

    const wanted = [index - 1, index, index + 1]
      .filter((i) => i >= 0 && i < realCount) // also skips synthetic (i === realCount)
      .map((i) => slideNumbers[i])
      .filter((n) => n != null);

    (async () => {
      for (const n of wanted) {
        if (urlsByNumber[n]) continue;
        try {
          const url = await fetchSlideBlob(slideUrl(trainingId, n));
          if (cancelled) {
            URL.revokeObjectURL(url);
            return;
          }
          setUrlsByNumber((prev) => (prev[n] ? prev : { ...prev, [n]: url }));
        } catch (e) {
          if (!cancelled) setError(e.message);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [trainingId, index, realCount, slideNumbers, slideUrl, urlsByNumber]);

  // Revoke blob URLs on unmount. Uses urlsRef so the cleanup sees the final
  // set of URLs, not the empty object captured at mount-time.
  useEffect(() => {
    return () => {
      Object.values(urlsRef.current).forEach((u) => URL.revokeObjectURL(u));
    };
  }, []);

  // Language (or any other deck variant) changed: the cached images belong to the
  // previous deck, so revoke them and let the loader re-fetch at the same index.
  useEffect(() => {
    setUrlsByNumber((prev) => {
      Object.values(prev).forEach((u) => URL.revokeObjectURL(u));
      return {};
    });
  }, [variantKey]);

  const setAnim = useCallback((v) => {
    animatingRef.current = v;
    setAnimating(v);
  }, []);

  const snapBack = useCallback(() => {
    setAnim(true);
    setDragX(0);
    setTimeout(() => setAnim(false), COMMIT_MS);
  }, [setAnim]);

  const commit = useCallback((delta, targetDx) => {
    setAnim(true);
    setDragX(targetDx);
    setTimeout(() => {
      // Reset drag and animation in the same tick that we advance the index,
      // so the strip doesn't visibly rewind before the new center settles.
      setAnim(false);
      setDragX(0);
      onIndexChange(index + delta);
    }, COMMIT_MS);
  }, [index, onIndexChange, setAnim]);

  const animateTo = useCallback((newIndex) => {
    if (newIndex === index || animatingRef.current) return;
    if (newIndex < 0 || newIndex >= total) return;
    const width = containerWidth.current || 1;
    commit(newIndex - index, newIndex > index ? -width : width);
  }, [index, total, commit]);

  // Keyboard nav routes through animateTo so feel matches swipe.
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight') animateTo(index + 1);
      if (e.key === 'ArrowLeft') animateTo(index - 1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [index, animateTo]);

  const handleTouchStart = (e) => {
    if (e.touches.length !== 1 || animatingRef.current) return;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    directionLock.current = null;
  };

  const handleTouchMove = (e) => {
    if (touchStartX.current == null) return;
    const t = e.touches[0];
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - touchStartY.current;

    // Decide the gesture direction once enough motion has accumulated, then keep it
    // for the rest of the gesture — avoids jittery flipping between horiz/vert.
    if (directionLock.current == null && Math.abs(dx) + Math.abs(dy) > 8) {
      directionLock.current = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
    }
    if (directionLock.current !== 'h') return; // let vertical scroll pass through

    const atLeft = index === 0;
    const atRight = index === total - 1;
    const damped = (dx > 0 && atLeft) || (dx < 0 && atRight) ? dx * 0.3 : dx;
    setDragX(damped);
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX.current;
    touchStartX.current = null;
    touchStartY.current = null;
    const wasHorizontal = directionLock.current === 'h';
    directionLock.current = null;
    if (!wasHorizontal) { setDragX(0); return; }

    const width = containerWidth.current || 1;
    const threshold = Math.min(80, width * 0.15);

    if (dx <= -threshold && index < total - 1) commit(+1, -width);
    else if (dx >= threshold && index > 0) commit(-1, width);
    else snapBack();
  };

  if (total === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-slate-400">
        {t('noSlidesUploaded')}
      </div>
    );
  }

  const hideNext = isSynthetic;

  const renderSlot = (logicalIndex, key) => {
    if (logicalIndex < 0 || logicalIndex >= total) {
      return <div key={key} className="w-1/3 flex-shrink-0" aria-hidden="true" />;
    }

    let inner;
    if (extraFinalSlide && logicalIndex === realCount) {
      inner = extraFinalSlide;
    } else {
      const n = slideNumbers[logicalIndex];
      const url = urlsByNumber[n];
      if (error && logicalIndex === index) {
        inner = <div className="text-rose-300">{error}</div>;
      } else if (!url) {
        inner = <div className="text-slate-400">{t('loadingSlide')}</div>;
      } else {
        inner = (
          <img
            src={url}
            alt={`Slide ${n}`}
            draggable="false"
            className="max-h-full max-w-full object-contain shadow-2xl pointer-events-none"
          />
        );
      }
    }

    return (
      <div
        key={key}
        className="w-1/3 flex-shrink-0 flex items-center justify-center bg-black p-6 overflow-auto"
      >
        {inner}
      </div>
    );
  };

  return (
    <div className="flex-1 flex flex-col">
      <div
        ref={containerRef}
        className="flex-1 relative overflow-hidden bg-black select-none"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="absolute inset-0 flex"
          style={{
            width: '300%',
            transform: `translate3d(calc(-33.3333% + ${dragX}px), 0, 0)`,
            transition: animating ? `transform ${COMMIT_MS}ms ease-out` : 'none',
            willChange: 'transform',
          }}
        >
          {renderSlot(index - 1, 'prev')}
          {renderSlot(index, 'curr')}
          {renderSlot(index + 1, 'next')}
        </div>
      </div>
      <div className="flex items-center justify-between p-4 bg-slate-900 border-t border-slate-700">
        <button
          type="button"
          onClick={() => animateTo(index - 1)}
          disabled={index === 0 || animating}
          className="px-4 py-2 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
        >
          <ChevronLeft className="w-4 h-4" /> {t('navPrevious')}
        </button>
        <div className="text-sm text-slate-300">
          {t('slideOf', { current: index + 1, total })}
          <div className="w-64 h-1.5 bg-slate-700 rounded-full mt-2 overflow-hidden">
            <div
              className="h-full bg-amber-500 transition-all"
              style={{ width: `${((index + 1) / total) * 100}%` }}
            />
          </div>
        </div>
        {hideNext ? (
          // Keep the layout balanced when Next is hidden (synthetic pre-quiz slide).
          <div className="w-[6.5rem]" aria-hidden="true" />
        ) : (
          <button
            type="button"
            onClick={() => animateTo(index + 1)}
            disabled={index === total - 1 || animating}
            className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
          >
            {t('navNext')} <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default SlideViewer;
